import { useCallback, useState } from 'react';
import { components } from 'react-select';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { User } from '../../../clients/quipli-service/user/user';
import Icon from '../../../components/icons/icon';
import { CreateUserModal } from './create-user-modal';
import { AsyncCreatableSelect } from '../../../components/select-form/select-form';
import { Typography } from '../../../components/typography/typography';
import { formatPhoneNumber } from '../../../util/util';

export const CustomerSelect = ({
  setCustomer,
  disableScanning = false,
}: {
  setCustomer: (c: User | null) => void;
  disableScanning?: boolean;
}) => {
  const { searchUsers } = useAPI();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>(true);

  // Callbacks
  const search = useCallback(
    debounce((keyword: string, callback) => {
      searchUsers({
        keyword,
        pageNumber: 1,
        pageSize: 30,
        cashCustomerFirst: true,
      }).then((res) => {
        callback(res.items);
        setIsLoadingInitial(false); // Just want this for the initial load
      });
    }, 300),
    [],
  );

  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const onUserSelected = (user: User | null) => {
    setSelectedUser(user);
    setCustomer(user);
  };

  return (
    <>
      <AsyncCreatableSelect<User>
        cacheOptions
        defaultOptions
        loadOptions={search}
        isLoading={isLoadingInitial}
        onClickCreatableOption={() => setIsModalOpen(true)}
        getOptionLabel={(op) => op.name}
        getOptionValue={(op) => `${op.id}`}
        onChange={(value) => onUserSelected(value)}
        loadingMessage={() => 'Loading customers...'}
        isSearchable={!isLoadingInitial}
        isDisabled={isLoadingInitial}
        noOptionsMessage={() => 'No customers found'}
        dataCy="customer-select"
        value={selectedUser}
        components={{
          Placeholder: (props) => (
            <components.Placeholder {...props}>
              <Typography type="text-sm">Search customers</Typography>
            </components.Placeholder>
          ),
          ValueContainer: (props) => (
            <div className="flex items-center gap-[6px] pl-[10px]">
              <Icon icon="Search" size="medium" stroke="var(--grey-500)" />
              <components.ValueContainer {...props} />
            </div>
          ),
          Option: (props) => {
            //@ts-expect-error __isNew__
            const isCreateOption = props.data.__isNew__ === true;

            return (
              <components.Option {...props}>
                <div
                  className={classNames('flex items-center gap-[6px]', {
                    'justify-between': !isCreateOption,
                  })}
                >
                  {isCreateOption ? (
                    <>
                      <div className="quipli-v2 flex items-center">
                        <Icon icon="Plus" stroke="var(--quipli-black-800)" />
                      </div>
                      <Typography dataCy="add-customer" type="text-md" weight="medium" className="text-grey-900">
                        Add new customer
                      </Typography>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col gap-[4px]">
                        <Typography type="text-md" weight="medium" className="text-grey-900">
                          {props.children}
                        </Typography>
                        <div>
                          <Typography type="text-sm" className="text-grey-500">
                            {formatPhoneNumber(props.data.phone)}
                          </Typography>
                          <Typography type="text-sm" className="text-grey-500">
                            {props.data.email}
                          </Typography>
                          {props.data.doNoRent && (
                            <Typography type="text-sm" className="text-error-500">
                              Customer has DNR flag!
                            </Typography>
                          )}
                          {props.data.paymentBan && (
                            <Typography type="text-sm" className="text-error-500">
                              Customer has a payment ban!
                            </Typography>
                          )}
                        </div>
                      </div>
                      {props.isSelected && <Icon icon="Check" stroke="var(--quipli-black-800)" />}
                    </>
                  )}
                </div>
              </components.Option>
            );
          },
        }}
      />

      <CreateUserModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        setNewUser={onUserSelected}
        disableScanning={disableScanning}
      />
    </>
  );
};
